import type { useI18n } from 'vue-i18n';
import type { FormatLocale } from '#layers/base/types/locale';
import type { NativeDateInput } from './dates';
import { Temporal } from 'temporal-polyfill';
import { translateString } from '#layers/i18n/public';
import { plainDateFromLocalDate, toNativeDate } from './dates';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_MINUTE_MS = 60 * 1000;
const DAYS_PER_WEEK = 7;
const relativeTimeFormatters = new Map<string, Intl.RelativeTimeFormat>();

/** Formatter construction is not free and these run per row in tables, so cache one formatter per locale and numeric style. */
function getRelativeTimeFormat(
	locale: FormatLocale,
	numeric: Intl.RelativeTimeFormatNumeric = 'always',
): Intl.RelativeTimeFormat {
	const cacheKey = `${locale}:${numeric}`;
	let formatter = relativeTimeFormatters.get(cacheKey);

	if (!formatter) {
		formatter = new Intl.RelativeTimeFormat(locale, { style: 'long', numeric });
		relativeTimeFormatters.set(cacheKey, formatter);
	}

	return formatter;
}

/**
 * Derives today from `Date` rather than `Temporal.Now`, which reads the host clock directly under a native Temporal
 * implementation and so ignores the fake timers every caller's tests rely on.
 */
function getCurrentLocalPlainDate(): Temporal.PlainDate {
	return plainDateFromLocalDate(new Date());
}

export interface FormatRelativeDateOptions {
	/**
	 * Pass `formatLocale` from `useUserLocale()`. `Intl.RelativeTimeFormat` is a formatter like
	 * any other, so it follows the region setting, not the UI language.
	 */
	locale: FormatLocale;
	/** Caps bucketing at days for callers that need exact remaining runway, where "in 1 year" could mean anything from 365 to 729 days. */
	maxUnit?: 'day';
}

export interface FormatRelativeTimeOptions extends FormatRelativeDateOptions {
	/** Resolve down to minutes and hours. Only meaningful for values that carry a time of day. */
	granularity?: 'minute';
}

/**
 * `i18n` still carries the just-now copy; everything numeric goes through `options.locale`.
 */
export function formatRelativeTime(
	date: NativeDateInput,
	i18n: ReturnType<typeof useI18n>,
	options: FormatRelativeTimeOptions,
): string {
	const nativeDate = toNativeDate(date);

	if (options.granularity !== 'minute') {
		return formatRelativePlainDate(plainDateFromLocalDate(nativeDate), options);
	}

	return formatRelativeEpochMilliseconds(nativeDate.getTime(), i18n, options);
}

/**
 * A calendar date has no time of day, so this resolves to whole days and takes no granularity. It also needs no `i18n`:
 * every word it produces comes from `Intl`, not the message catalog.
 */
export function formatRelativePlainDate(
	date: Temporal.PlainDate,
	options: FormatRelativeDateOptions,
): string {
	const capAtDays = options.maxUnit === 'day';
	// Calendar arithmetic keeps months and years honest across varying month lengths, so a year never renders as "12 months".
	// Capping at days collapses years and months into the day count, leaving only the week bucket to skip explicitly.
	const { years, months, days } = date.since(getCurrentLocalPlainDate(), { largestUnit: capAtDays ? 'day' : 'year' });
	const absoluteDays = Math.abs(days);
	const relativeTimeFormat = getRelativeTimeFormat(options.locale);

	if (years !== 0) {
		return relativeTimeFormat.format(years, 'years');
	}

	if (months !== 0) {
		return relativeTimeFormat.format(months, 'months');
	}

	if (!capAtDays && absoluteDays >= DAYS_PER_WEEK) {
		return relativeTimeFormat.format(Math.trunc(days / DAYS_PER_WEEK), 'weeks');
	}

	// Only today and the adjacent days read better as words; further out, "last week" would hide how far back a value really is.
	if (absoluteDays <= 1) {
		return getRelativeTimeFormat(options.locale, 'auto').format(days, 'days');
	}

	return relativeTimeFormat.format(days, 'days');
}

export function formatRelativePlainDateTime(
	dateTime: Temporal.PlainDateTime,
	i18n: ReturnType<typeof useI18n>,
	options: FormatRelativeTimeOptions,
): string {
	if (options.granularity !== 'minute') {
		return formatRelativePlainDate(dateTime.toPlainDate(), options);
	}

	const instant = dateTime.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
	return formatRelativeTime(instant, i18n, options);
}

function formatRelativeEpochMilliseconds(
	epochMilliseconds: number,
	i18n: ReturnType<typeof useI18n>,
	options: FormatRelativeTimeOptions,
): string {
	const difference = epochMilliseconds - Date.now();
	const absoluteDifference = Math.abs(difference);

	if (absoluteDifference < ONE_MINUTE_MS) {
		return translateString('$t:time.just_now', i18n).toLocaleLowerCase();
	}

	// Truncating keeps the label within the elapsed range it describes; rounding would age a 90 second old value to "2 minutes ago".
	if (absoluteDifference < ONE_HOUR_MS) {
		return getRelativeTimeFormat(options.locale).format(Math.trunc(difference / ONE_MINUTE_MS), 'minutes');
	}

	const date = plainDateFromLocalDate(new Date(epochMilliseconds));

	// Hours also cover a DST-lengthened local day, which holds more than 24 elapsed hours on a single calendar date:
	// a day word there would contradict the exact timestamp shown alongside.
	if (absoluteDifference < ONE_DAY_MS || date.equals(getCurrentLocalPlainDate())) {
		return getRelativeTimeFormat(options.locale).format(Math.trunc(difference / ONE_HOUR_MS), 'hours');
	}

	return formatRelativePlainDate(date, options);
}
